import { useQuery } from 'react-query';
import useAmplitude from 'providers/root/AmplitudeProvider/useAmplitude';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { ChartBlockConfig } from '@reports/domain/value-objects/blocks/ChartBlockConfig';
import { useReportsState } from '@reports/presentation/contexts/useReportsState';
import { buildReportBlockAnalytics } from '@reports/presentation/utils';
import { getBlocksUseCases } from '@reports/composition';

export type UseChartBlockProps = {
  request: BlockDataRequestDto;
  blockConfig: ChartBlockConfig;
  enabled?: boolean;
};

/**
 * Hook to fetch chart block data
 *
 * Uses React Query to fetch and cache chart block data based on the provided
 * block configuration. The endpoint is extracted from the blockConfig.
 * Gets blocks use cases from composition root.
 *
 * @param {Object} props - Hook properties
 * @param {BlockDataRequestDto} props.request - Request filters and date ranges
 * @param {ChartBlockConfig} props.blockConfig - Chart block configuration instance
 * @param {boolean} [props.enabled=true] - Whether the query should be enabled
 * @returns {Object} Chart block data, error, state and refetch function
 *
 * @example
 * const { chartBlockData, chartBlockError, chartBlockState, refetchChartBlock } = useReportChartBlock({
 *   request: { startDate: new Date('2025-01-01'), endDate: new Date('2025-01-02'), ... },
 *   blockConfig: chartBlockConfig,
 *   enabled: true
 * });
 */
export const useReportChartBlock = ({ request, blockConfig, enabled = true }: UseChartBlockProps) => {
  const { fetchChartBlock } = getBlocksUseCases();
  const { state } = useReportsState();
  const { logEvent } = useAmplitude();

  const { data, error, refetch, isError, isFetching, isSuccess } = useQuery({
    queryKey: ['reports', 'block', blockConfig.key, request],
    queryFn: () => fetchChartBlock.execute(request, blockConfig.endpoint),
    refetchOnWindowFocus: false,
    retry: false,
    enabled,
    onSuccess: () => {
      const reportName = state.currentReport?.type || 'unknown';
      const analyticsData = buildReportBlockAnalytics(request, blockConfig.key, reportName);
      logEvent('@event.$reports.reportViewed', `@@section.${state.currentReport?.type}`, analyticsData);
    },
  });

  return {
    chartBlockData: data,
    chartBlockError: error,
    chartBlockState: { isFetching, isError, isSuccess },
    refetchChartBlock: refetch,
  };
};
